import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useWallet } from '@solana/wallet-adapter-react'
import { useSelector } from 'react-redux'
import type { RootState } from '../../store'
import { useWeb3Context } from '../../contexts/web3Context'
import { linkVirtualAccountToSolanaWallet } from '../../utils/linkVirtualWalletToSolana'
import { alert } from '../Alert/Alert'

/** Must match backend `packages/core/src/utils/virtualWalletAddress.js` */
const STABLE_VIRTUAL_WALLET_PREFIX = 'eclip_vw_'

/**
 * When an email account connects Phantom (etc.), sign once to link or **replace** the linked wallet
 * (e.g. new browser without the old extension) — then reload.
 */
export function AutoLinkVirtualSolanaWallet() {
  const { t } = useTranslation()
  const user = useSelector((s: RootState) => s.userModule.user)
  const { publicKey, signMessage, connected } = useWallet()
  const { setSessionWalletFromLogin } = useWeb3Context()
  const inFlight = useRef(false)
  const failedPair = useRef<string | null>(null)

  useEffect(() => {
    if (!connected) failedPair.current = null
  }, [connected])

  useEffect(() => {
    const stableAccountId = user?.walletAddress
    if (!stableAccountId?.startsWith(STABLE_VIRTUAL_WALLET_PREFIX)) return

    const linkedReal = (user?.realWalletAddress && String(user.realWalletAddress).trim()) || ''

    const needsFirstLink = user?.wallet_type === 'virtual'
    const needsReplaceLinkedWallet = user?.wallet_type === 'real' && !!linkedReal

    if (!needsFirstLink && !needsReplaceLinkedWallet) return
    if (!connected || !publicKey || !signMessage) return

    const sol = publicKey.toBase58()
    /* Same device/browser as stored link — no API call */
    if (needsReplaceLinkedWallet && sol === linkedReal) return

    const pair = `${stableAccountId}|${sol}`
    if (failedPair.current === pair) return
    if (inFlight.current) return

    inFlight.current = true
    let cancelled = false

    void (async () => {
      try {
        const res = await linkVirtualAccountToSolanaWallet(stableAccountId, publicKey, signMessage)
        if (cancelled) return
        if (!res.success) {
          alert.open(res.message)
          failedPair.current = pair
          return
        }
        // Keep stable game id as API identity after link (same as email login session).
        setSessionWalletFromLogin(stableAccountId)
        alert.open(t('emailAuth.linkWallet.linkedReloading'))
        window.location.reload()
      } catch (e: unknown) {
        if (cancelled) return
        const msg =
          (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
          (e as Error)?.message ||
          t('emailAuth.linkWallet.linkFailed')
        alert.open(msg)
        failedPair.current = pair
      } finally {
        if (!cancelled) inFlight.current = false
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    user?.wallet_type,
    user?.walletAddress,
    user?.realWalletAddress,
    connected,
    publicKey,
    signMessage,
    setSessionWalletFromLogin,
    t,
  ])

  return null
}
